import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { titleOf, extOf } from "../utils/fileUtils";

// Configurer le worker pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function PreviewPanel({ preview, isFullscreen }) {
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);

  const previewRef = useRef(null);
  const previewContainerRef = useRef(null);
  const videoRef = useRef(null);

  // Reset state when preview changes
  useEffect(() => {
    setPdfPage(1);
    setPdfScale(1.0);
    setPdfPages(0);
  }, [preview]);

  const fullscreenPreview = () => {
    if (previewContainerRef.current) {
      if (previewContainerRef.current.requestFullscreen) {
        previewContainerRef.current.requestFullscreen();
      } else if (previewContainerRef.current.webkitRequestFullscreen) {
        previewContainerRef.current.webkitRequestFullscreen();
      } else if (previewContainerRef.current.msRequestFullscreen) {
        previewContainerRef.current.msRequestFullscreen();
      }
    }
  };

  if (!preview?.doc || !preview?.url) {
    return <div className="mt-3 text-muted">Sélectionnez un fichier pour le lire ici.</div>;
  }

  const c = preview.doc;
  const url = preview.url;
  const ext = extOf(c);

  const commonBox = {
    position: "relative",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
    background: "#000",
    minHeight: 300,
    maxHeight: isFullscreen ? '100vh' : 640,
  };

  // --- Vidéo ---
  if (["mp4","webm","mov","m4v"].includes(ext)) {
    return (
      <div className="mt-4" ref={previewRef}>
        <div
          className="ratio ratio-16x9"
          ref={previewContainerRef}
        >
          <video
            src={url}
            controls
            preload="metadata"
            // Removed controlsList="nodownload noplaybackrate" to enable fullscreen and other controls
            ref={videoRef}
            disablePictureInPicture
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
    );
  }

  // --- Audio ---
  if (["mp3","wav"].includes(ext)) {
    return (
      <div className="mt-4" ref={previewRef}>
        <div style={{ ...commonBox, background: "#fff", minHeight: 120 }} ref={previewContainerRef}>
          <audio
            src={url}
            controls
            controlsList="nodownload noplaybackrate"
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  }

  // --- Image ---
  if (["png","jpg","jpeg","gif"].includes(ext)) {
    return (
      <div className="mt-4" ref={previewRef}>
        <div style={{ ...commonBox, background: "#111" }} ref={previewContainerRef}>
          <img
            src={url}
            alt={titleOf(c)}
            draggable={false}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              maxHeight: 640,
              objectFit: "contain",
              userSelect: "none",
              WebkitUserDrag: "none",
              WebkitTouchCallout: "none",
            }}
            onContextMenu={(e) => e.preventDefault()}
          />
          <div
            style={{ position: "absolute", inset: 0, WebkitTouchCallout: "none", userSelect: "none" }}
            onContextMenu={(e)=>e.preventDefault()}
          />
        </div>
        <div className="mt-2 d-flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={fullscreenPreview}>Plein écran</button>
        </div>
      </div>
    );
  }

  // --- PDF ---
  return (
    <div className="mt-4" ref={previewRef}>
      <div style={{ ...commonBox, background: "#fff" }} ref={previewContainerRef}>
        <div className="w-100" style={{ overflow: "auto", maxHeight: 640 }}>
          <PdfCanvasViewerUrl
            url={url}
            scale={pdfScale}
            pageNumber={pdfPage}
            isFullscreen={isFullscreen}
            onLoaded={(n) => { setPdfPages(n || 0); }}
          />
        </div>
        {isFullscreen && (
          <div
            className="fs-toolbar"
            style={{
              position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10000, background: 'rgba(255,255,255,0.95)',
              border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: 6, display: 'flex', gap: 6, alignItems: 'center'
            }}
          >
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPdfScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))}
              disabled={pdfScale >= 2.5}
              title="Zoom +"
            >
              Zoom +
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPdfScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))}
              disabled={pdfScale <= 0.6}
              title="Dézoom"
            >
              Dézoom
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                try {
                  const viewer = (previewContainerRef.current || previewRef.current)?.querySelector?.('.pdf-canvas-viewer');
                  const canvas = viewer?.querySelector?.('canvas');
                  if (!viewer || !canvas) return;
                  const baseWidth = canvas.width / pdfScale;
                  const target = viewer.clientWidth - 16;
                  const newScale = Math.max(0.6, Math.min(2.5, +(target / baseWidth).toFixed(2)));
                  setPdfScale(newScale);
                } catch {}
              }}
              title="Ajuster à la largeur"
            >
              Ajuster
            </button>
            <div className="vr" style={{ width: 1, background: '#ddd', height: 24 }}></div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
              disabled={(pdfPages || 1) <= 1 || pdfPage <= 1}
              title="Page précédente"
            >
              ←
            </button>
            <span className="small">{pdfPage} / {Math.max(1, pdfPages || 1)}</span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPdfPage((p) => Math.min((pdfPages || 1), p + 1))}
              disabled={(pdfPages || 1) <= 1 || pdfPage >= (pdfPages || 1)}
              title="Page suivante"
            >
              →
            </button>
          </div>
        )}
      </div>
      <div className="mt-2 d-flex gap-2 align-items-center flex-wrap">
        <button className="btn btn-sm btn-primary" onClick={fullscreenPreview}>Plein écran</button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setPdfScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))}
          disabled={pdfScale >= 2.5}
          title="Zoom"
        >
          Zoom
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setPdfScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))}
          disabled={pdfScale <= 0.6}
          title="Dezoom"
        >
          Dezoom
        </button>
        <select
          className="form-select form-select-sm"
          style={{ width: 110 }}
          value={pdfScale}
          onChange={(e) => setPdfScale(Number(e.target.value))}
        >
          <option value={0.8}>80%</option>
          <option value={1.0}>100%</option>
          <option value={1.25}>125%</option>
          <option value={1.5}>150%</option>
          <option value={2.0}>200%</option>
        </select>
        <span className="small text-muted">x{pdfScale.toFixed(1)}</span>
        <div className="input-group input-group-sm" style={{ width: 180 }}>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
            disabled={(pdfPages || 1) <= 1 || pdfPage <= 1}
          >
            ← Prev
          </button>
          <input
            type="number"
            className="form-control"
            min={1}
            max={Math.max(1, pdfPages || 1)}
            value={pdfPage}
            onChange={(e) => {
              const v = Number(e.target.value || 1);
              setPdfPage(Math.max(1, Math.min(v, pdfPages || 1)));
            }}
          />
          <span className="input-group-text">/ {Math.max(1, pdfPages || 1)}</span>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => setPdfPage((p) => Math.min((pdfPages || 1), p + 1))}
            disabled={(pdfPages || 1) <= 1 || pdfPage >= (pdfPages || 1)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

// Viewer PDF basé sur canvas
function PdfCanvasViewerUrl({ url, scale = 1, pageNumber = 1, onLoaded, isFullscreen = false }) {
  const containerRef = useRef(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!url || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      const loadingTask = pdfjsLib.getDocument({ url });
      const pdf = await loadingTask.promise;
      if (cancel) return;
      try { onLoaded && onLoaded(pdf.numPages); } catch {}
      const pn = Math.max(1, Math.min(pageNumber || 1, pdf.numPages));
      const page = await pdf.getPage(pn);
      if (cancel) return;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { alpha: false });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.display = "block";
      canvas.style.margin = "12px auto";
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      canvas.oncontextmenu = (e) => e.preventDefault();
      containerRef.current.appendChild(canvas);
      await page.render({ canvasContext: ctx, viewport }).promise;
    })();
    return () => { cancel = true; };
  }, [url, scale, pageNumber, onLoaded]);
  return (
    <div
      ref={containerRef}
      className="pdf-canvas-viewer"
      style={{ maxHeight: isFullscreen ? '100vh' : 640, overflow: "auto", userSelect: "none" }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
